import React, { useCallback, useMemo } from 'react';

import { Grid } from '@/components/Grid';

import { MAX_NUM_OPTIONS } from '../../../hooks/use-market-line-selection';
import { Event, Market, Option } from '../../../types/index';
import { OptionButton, getBorderRadiusStyle } from '../../OptionButton';
import { EmptyOptionButton } from './EmptyOptionButton';

type OptionGridLayoutProps = {
    event: Event;
    market?: Market;
    columns: number;
    testID?: string;
    showAdditionalOptions?: boolean;
};

export const OptionGridLayout = ({ event, market, columns, showAdditionalOptions, testID }: OptionGridLayoutProps) => {
    const maxOptionsNum = MAX_NUM_OPTIONS;
    const totalRows = useMemo(
        () => Math.ceil((market?.options?.length ?? 0) / columns),
        [market?.options?.length, columns]
    );
    const renderItem = useCallback(
        (option: Option | null, row: number, col: number) => {
            const buttonStyles = getBorderRadiusStyle(row, col, totalRows, columns);
            return option && market ? (
                <OptionButton
                    key={option?.id} // Key is needed here to ensure a new option button is rendered which prevents the odds change animation from triggering
                    option={option}
                    market={market}
                    event={event}
                    testID={testID}
                    buttonStyle={buttonStyles}
                />
            ) : (
                <EmptyOptionButton key={`empty-${row}-${col}`} style={buttonStyles} />
            );
        },
        [event, market, testID, columns, totalRows]
    );
    // The MAX_NUM_OPTIONS cap only limits multi-column grid height (the rest revealed via "view more");
    // a single column is a stacked list that always shows every option.
    const showAllOptions = showAdditionalOptions || columns === 1;
    let options = showAllOptions ? market?.options : market?.options.slice(0, maxOptionsNum);

    // TODO: To remove this condition after the API is updated.
    if (options && market?.market_type?.code?.includes('HANDICAP')) {
        const sortOrder: { [key: string]: number } = { AWAY: 1, HOME: 2, DRAW: 3 };
        const getRank = (option: Option) => sortOrder[option.option_type.code] ?? Number.MAX_SAFE_INTEGER;
        options = options.toSorted((a, b) => getRank(a) - getRank(b));
    }

    return options ? <Grid columns={columns} data={options} spacing="s3" renderItem={renderItem} /> : null;
};
